import { useCallback, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useUnmount } from "react-use";
import { v4 } from "uuid";
import { storageOverusedError } from "~/store/actions/actions.httpMessages";
import { StorageOverusedError } from "~/utils/errors";
import Angie from "../../utils/angie.jsx";
import { fetch } from "../../utils/fetch.jsx";
import { createQueue } from "../../utils/queue.jsx";
import upload from "../../utils/upload.jsx";
import { useUploadContext } from "../UploadProvider/context/index.js";

const queue = createQueue();

export const useFileHandler = (options) => {
  const { context = "", successCallback, clearOnUnmount = true } = options ?? {};
  const { getStateByContext, getDispatch } = useUploadContext();

  const dispatch = getDispatch(context);
  const storeDispatch = useDispatch();
  const { uploads, uploading, uploaded, error } = getStateByContext(context);

  const fileRequestMap = useRef({});

  const dropUploadedFile = useCallback(
    ({ code }) => {
      dispatch({ type: "remove", code });
    },
    [dispatch]
  );

  const executeUploadSuccessCallback = useCallback(
    (files) => {
      const clear = () => {
        files.forEach(({ code }) => {
          dispatch({ type: "remove", code });
        });
      };

      typeof successCallback === "function" && successCallback({ files, clear, uploading: queue.isActive() });
    },
    [dispatch, successCallback]
  );

  const handleUploadSuccess = useCallback(
    (code, uploadedFile) => {
      queue.done().next();
      dispatch({
        type: "success",
        code,
        files: [uploadedFile],
        uploading: queue.isActive(),
      });
      executeUploadSuccessCallback([uploadedFile]);
    },
    [dispatch, executeUploadSuccessCallback]
  );

  const handleUploadError = useCallback(
    (code, error) => {
      queue.done().next();
      if (error instanceof StorageOverusedError) {
        storeDispatch(storageOverusedError(error));
      }
      dispatch({ type: "error", code, error, uploading: queue.isActive() });
    },
    [dispatch, storeDispatch]
  );

  const handleCancelUpload = useCallback(
    (code) => {
      const { request } = fileRequestMap.current[code] || {};

      if (request) {
        request.abort();
        delete fileRequestMap.current[code];
      }

      queue.cancel(code).next();

      dispatch({ type: "cancel", code, uploading: queue.isActive() });
    },
    [dispatch]
  );

  const handleUploadProgress = useCallback(
    (code, progress) => {
      dispatch({ type: "update", code, progress });
    },
    [dispatch]
  );

  const handleUploadStart = useCallback(
    (code) => {
      if (!fileRequestMap.current[code]) {
        return;
      }

      const xhr = new XMLHttpRequest();

      fileRequestMap.current[code].request = xhr;

      const { file } = fileRequestMap.current[code];

      const newUpload = upload(file, (progress) => handleUploadProgress(code, progress))(xhr);

      newUpload
        .then((response) => handleUploadSuccess(code, response))
        .catch((error) => {
          handleUploadError(code, error);
        });
    },
    [handleUploadError, handleUploadProgress, handleUploadSuccess]
  );

  const handleUpload = useCallback(
    (file) => {
      const code = v4();

      if (!fileRequestMap.current[code]) {
        fileRequestMap.current[code] = {};
      }

      fileRequestMap.current[code].file = file;

      dispatch({ type: "init", code, name: file.name });
      queue.add({ code, callback: handleUploadStart }).next();
    },
    [dispatch, handleUploadStart]
  );

  const handleRetryUpload = useCallback(
    (code) => {
      const { file } = fileRequestMap.current[code] || {};

      if (file) {
        queue.add({ code, callback: handleUploadStart }).next();
        dispatch({ type: "retry", code, uploading: queue.isActive() });
      }
    },
    [dispatch, handleUploadStart]
  );

  const handleAttachFromDropbox = useCallback(
    (files) => {
      fetch({
        url: `${Angie.api_url}/integrations/dropbox/batch`,
        method: `post`,
        body: { files },
      }).then((response) => {
        if (Array.isArray(response)) {
          dispatch({ type: "merge", files: response });
          executeUploadSuccessCallback(response);
        }
      });
    },
    [dispatch, executeUploadSuccessCallback]
  );

  const handleAttachFromGoogleDrive = useCallback(
    (docs) => {
      fetch({
        url: `${Angie.api_url}/integrations/google-drive/batch`,
        method: `post`,
        body: { docs },
      }).then((response) => {
        if (Array.isArray(response)) {
          dispatch({ type: "merge", files: response });
          executeUploadSuccessCallback(response);
        }
      });
    },
    [dispatch, executeUploadSuccessCallback]
  );

  const cancelAllUploads = useCallback(() => {
    const codes = Object.keys(uploads);

    codes.forEach((code) => {
      queue.cancel(code);
    });

    codes.forEach((code) => {
      const { request } = fileRequestMap.current[code] || {};

      if (request) {
        request.abort();
        delete fileRequestMap.current[code];
      }
    });

    dispatch({ type: "clear" });
  }, [dispatch, uploads]);

  useUnmount(() => {
    clearOnUnmount && cancelAllUploads();
  });

  useEffect(() => {
    const listener = (event) => {
      event?.detail?.files?.forEach(handleUpload);
    };

    document.addEventListener(`drop-files-${context}`, listener);

    return () => {
      document.removeEventListener(`drop-files-${context}`, listener);
    };
  }, [context, handleUpload]);

  return {
    uploads,
    uploading,
    uploaded,
    onUpload: handleUpload,
    onCancelUpload: handleCancelUpload,
    onRetryUpload: handleRetryUpload,
    onAttachFromDropbox: handleAttachFromDropbox,
    onAttachFromGoogleDrive: handleAttachFromGoogleDrive,
    cancelAllUploads,
    error,
    dropUploadedFile,
  };
};
